/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { SettingOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';

import { ContentContainer } from '@/components/ContentContainer';
import { HTTPError } from '@/components/HTTPError';
import { PageHeader } from '@/components/PageHeader';
import { useTenantReleasesQuery } from '@/hooks/query/useTenantReleasesQuery';
import { useAuthorization } from '@/hooks/useAuthorization';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { DownloadRelease } from '@/pages/DownloadRelease/DownloadRelease';
import { Button } from '@/primitives/Button';
import { Card } from '@/primitives/Card';
import { Flex } from '@/primitives/Flex';
import { OsIcon } from '@/primitives/Icons';
import { Skeleton } from '@/primitives/Skeleton';
import { Text } from '@/primitives/Text';
import { Title } from '@/primitives/Title';

const OS_NAMES = ['windows', 'linux'] as const;
type OSName = (typeof OS_NAMES)[number];

export function DownloadReleasePage() {
  const { osName, tenantID } = useParams<{ osName: string; tenantID: string }>();
  const { t } = useMimicTranslation('downloadNode');
  const navigate = useNavigate();
  const isSuperadmin = useAuthorization('superadmin');

  const validOS = Boolean(osName && OS_NAMES.includes(osName as OSName));
  const platform = validOS ? (osName as OSName) : undefined;

  const { data, isLoading } = useTenantReleasesQuery({
    osName: platform,
  });

  if (!validOS || !platform) {
    return <HTTPError httpStatus="404" />;
  }

  const pageHeader = (
    <PageHeader title={t(`${platform}.title`)} level={2} text={<Text>{t(`${platform}.caption`)}</Text>} />
  );

  if (isLoading || !data) {
    return (
      <ContentContainer width="100%">
        {pageHeader}
        <Skeleton loading />
      </ContentContainer>
    );
  }

  const releases = data[platform]?.allReleases ?? [];

  if (releases.length === 0) {
    return (
      <ContentContainer width="100%">
        {pageHeader}
        <Card style={{ width: '100%', marginTop: 24 }} padding="xxl">
          <div style={{ display: 'flex', justifyContent: 'center', width: '100%', padding: '48px 24px' }}>
            <Flex vertical align="center" gap="md">
              <div
                style={{
                  fontSize: 80,
                  opacity: 0.15,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: 16,
                }}
              >
                <OsIcon name={platform} />
              </div>
              <Title level={3} style={{ margin: 0, textAlign: 'center' }}>
                {t('emptyState.title')}
              </Title>
              <Text type="subtle" size="lg" style={{ textAlign: 'center', maxWidth: 400 }}>
                {isSuperadmin ? t('emptyState.superadminSubtitle') : t('emptyState.userSubtitle')}
              </Text>
              {isSuperadmin && (
                <Button
                  type="primary"
                  icon={<SettingOutlined />}
                  onClick={() => navigate(`/tenants/${tenantID}/release-management`)}
                  style={{ marginTop: 16, height: 40, paddingLeft: 24, paddingRight: 24 }}
                >
                  {t('emptyState.goToReleaseManagement')}
                </Button>
              )}
            </Flex>
          </div>
        </Card>
      </ContentContainer>
    );
  }

  return (
    <ContentContainer width="100%">
      {pageHeader}
      <DownloadRelease osName={platform} tenantReleases={releases} />
    </ContentContainer>
  );
}
